import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { validateDiscount } from "../../Redux/Actions/DiscountActions";
import { DISCOUNT_VALIDATE_RESET } from "../../Redux/Constants/DiscountConstants";

const DiscountInput = ({ itemsPrice, onDiscountApplied }) => {
  const dispatch = useDispatch();
  const [code, setCode] = useState("");
  const [applied, setApplied] = useState(false);

  const { loading, result, error } = useSelector((s) => s.discountValidate);

  // ── When the Redux slice is reset externally (e.g. after order placement),
  //    result becomes null — use that as the signal to wipe local state too
  useEffect(() => {
    if (!result && !loading && !error) {
      setCode("");
      setApplied(false);
      onDiscountApplied(null);
    }
  }, [result]);   // eslint-disable-line react-hooks/exhaustive-deps

  // ── Mark as applied once server confirms validity ─────────────────────────
  useEffect(() => {
    if (result?.valid) {
      setApplied(true);
      onDiscountApplied({
        code:        result.discount.code,
        discountId:  result.discount._id,
        savings:     result.savings,
        finalAmount: result.finalAmount,
      });
    }
  }, [result]);   // eslint-disable-line react-hooks/exhaustive-deps

  // ── Server error clears applied state ─────────────────────────────────────
  useEffect(() => {
    if (error) {
      setApplied(false);
      onDiscountApplied(null);
    }
  }, [error]);   // eslint-disable-line react-hooks/exhaustive-deps

  const handleApply = () => {
    if (!code.trim()) return;
    dispatch(validateDiscount(code.trim(), itemsPrice));
  };

  const handleRemove = () => {
    setCode("");
    setApplied(false);
    dispatch({ type: DISCOUNT_VALIDATE_RESET });
    onDiscountApplied(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !applied) handleApply();
  };

  return (
    <div
      style={{
        border: "1px solid #dee2e6",
        borderRadius: 8,
        padding: "14px 16px",
        marginBottom: 16,
        background: "#fff",
      }}
    >
      <p
        style={{
          fontSize: 13,
          fontWeight: 700,
          marginBottom: 10,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          color: "#495057",
        }}
      >
        🏷️ Discount Code
      </p>

      <div style={{ display: "flex", gap: 8 }}>
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          onKeyDown={handleKeyDown}
          placeholder="Enter code"
          disabled={applied || loading}
          style={{
            flex: 1,
            padding: "8px 12px",
            border: `1.5px solid ${
              error ? "#dc3545" : applied ? "#198754" : "#ced4da"
            }`,
            borderRadius: 6,
            fontSize: 14,
            fontFamily: "monospace",
            letterSpacing: "0.06em",
            fontWeight: 600,
            outline: "none",
            background: applied ? "#f0fdf4" : "#fff",
            color: applied ? "#198754" : "#212529",
            transition: "border-color 0.2s",
          }}
        />

        {!applied ? (
          <button
            onClick={handleApply}
            disabled={loading || !code.trim()}
            style={{
              padding: "8px 18px",
              background:
                loading || !code.trim()
                  ? "#6c757d"
                  : "linear-gradient(135deg, #198754, #157347)",
              border: "none",
              borderRadius: 6,
              color: "#fff",
              fontSize: 13,
              fontWeight: 700,
              cursor: loading || !code.trim() ? "not-allowed" : "pointer",
              whiteSpace: "nowrap",
              display: "flex",
              alignItems: "center",
              gap: 6,
              transition: "background 0.2s",
            }}
          >
            {loading ? (
              <>
                <span
                  style={{
                    width: 12,
                    height: 12,
                    border: "2px solid rgba(255,255,255,0.3)",
                    borderTop: "2px solid #fff",
                    borderRadius: "50%",
                    display: "inline-block",
                    animation: "spin 0.7s linear infinite",
                  }}
                />
                Checking…
              </>
            ) : (
              "Apply"
            )}
          </button>
        ) : (
          <button
            onClick={handleRemove}
            style={{
              padding: "8px 18px",
              background: "linear-gradient(135deg, #dc3545, #b02a37)",
              border: "none",
              borderRadius: 6,
              color: "#fff",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Remove
          </button>
        )}
      </div>

      {error && !applied && (
        <div
          style={{
            marginTop: 8,
            fontSize: 12,
            color: "#dc3545",
            display: "flex",
            alignItems: "center",
            gap: 5,
          }}
        >
          ✕ {error}
        </div>
      )}

      {applied && result && (
        <div
          style={{
            marginTop: 10,
            padding: "8px 12px",
            background: "#f0fdf4",
            border: "1px solid #bbf7d0",
            borderRadius: 6,
            fontSize: 13,
            color: "#166534",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>
            ✓ <strong>{result.discount.code}</strong> —{" "}
            {result.discount.type === "percentage"
              ? `${result.discount.value}% off`
              : `$${result.discount.value} off`}
          </span>
          <span style={{ fontWeight: 700 }}>−${result.savings?.toFixed(2)}</span>
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default DiscountInput;